import { Text, View } from "@react-pdf/renderer"

/** Default avg char width ÷ font size for Helvetica regular. */
export const HELVETICA_CHAR_WIDTH_RATIO = 0.5

/** Conservative avg char width for uppercase customer names at large sizes. */
export const HELVETICA_UPPERCASE_CHAR_WIDTH_RATIO = 0.55

/** Default avg char width ÷ font size for Helvetica bold. */
export const HELVETICA_BOLD_CHAR_WIDTH_RATIO = 0.55

const ELLIPSIS = "..."

/** Slightly under-report fit width so pre-split lines are not re-wrapped by react-pdf. */
const SPLIT_MAX_WIDTH_FACTOR = 0.94

function resolveStyle(style) {
    if (!style) {
        return {}
    }
    const styles = Array.isArray(style) ? style : [style]
    return Object.assign({}, ...styles.filter(Boolean))
}

function estimateCharsPerLine(fontSizePt, maxWidthPt, options = {}) {
    const ratio = options.charWidthRatio ?? HELVETICA_CHAR_WIDTH_RATIO
    const avgCharWidth = fontSizePt * ratio
    return Math.max(1, Math.floor(maxWidthPt / avgCharWidth))
}

function estimateTextWidth(text, fontSizePt, charWidthRatio) {
    return String(text).length * fontSizePt * charWidthRatio
}

function splitTextByCharacter(text, charsPerLine) {
    const value = String(text ?? "")
    if (!value) {
        return [""]
    }
    const limit = Math.max(1, charsPerLine)
    if (value.length <= limit) {
        return [value]
    }
    const lines = []
    for (let i = 0; i < value.length; i += limit) {
        lines.push(value.slice(i, i + limit))
    }
    return lines
}

function lastIndexMatching(segment, predicate) {
    for (let i = segment.length - 1; i >= 0; i--) {
        if (predicate(segment[i], i)) {
            return i
        }
    }
    return -1
}

/** Pick a split index within segment [0, fitEnd), preferring space → hyphen → non-alphanumeric → hard break. */
function findPreferredSplitIndex(text, fitEnd) {
    const end = Math.max(0, Math.min(fitEnd, text.length))
    if (end === 0) {
        return Math.min(1, text.length)
    }

    const segment = text.slice(0, end)

    let index = lastIndexMatching(segment, (char) => char === " ")
    if (index >= 0) {
        return index + 1
    }

    index = lastIndexMatching(segment, (char) => char === "-")
    if (index >= 0) {
        return index + 1
    }

    index = lastIndexMatching(segment, (char) => char !== " " && char !== "-" && !/[a-zA-Z0-9]/.test(char))
    if (index >= 0) {
        return index + 1
    }

    return end
}

function findMaxFitEnd(text, maxWidth, fontSize, charWidthRatio) {
    for (let end = text.length; end > 0; end--) {
        if (estimateTextWidth(text.slice(0, end), fontSize, charWidthRatio) <= maxWidth) {
            return end
        }
    }
    return Math.min(1, text.length)
}

function truncateLineWithEllipsis(text, { maxWidth, fontSize, charWidthRatio, forceEllipsis = false }) {
    const value = String(text ?? "").trim()
    if (!value) {
        return ELLIPSIS
    }

    if (!forceEllipsis && estimateTextWidth(value, fontSize, charWidthRatio) <= maxWidth) {
        return value
    }

    for (let end = value.length; end >= 0; end--) {
        const candidate = `${value.slice(0, end).trimEnd()}${ELLIPSIS}`
        if (estimateTextWidth(candidate, fontSize, charWidthRatio) <= maxWidth) {
            return candidate
        }
    }

    return ELLIPSIS
}

function splitTextToLinesUnlimited(text, { maxWidth, fontSize, charWidthRatio = HELVETICA_CHAR_WIDTH_RATIO }) {
    const fitWidth = effectiveMaxWidth(maxWidth)
    let remaining = String(text ?? "").trim()
    if (!remaining) {
        return [""]
    }

    const lines = []

    while (remaining.length > 0) {
        if (estimateTextWidth(remaining, fontSize, charWidthRatio) <= fitWidth) {
            lines.push(remaining)
            break
        }

        const fitEnd = findMaxFitEnd(remaining, fitWidth, fontSize, charWidthRatio)
        const splitIndex = findPreferredSplitIndex(remaining, fitEnd)
        const line = remaining.slice(0, splitIndex).trimEnd()

        if (!line) {
            lines.push(remaining.slice(0, 1))
            remaining = remaining.slice(1).trimStart()
            continue
        }

        lines.push(line)
        remaining = remaining.slice(splitIndex).trimStart()
    }

    return lines.length > 0 ? lines : [""]
}

function effectiveMaxWidth(maxWidth) {
    return maxWidth * SPLIT_MAX_WIDTH_FACTOR
}

function splitTextToLines(text, { maxWidth, fontSize, charWidthRatio = HELVETICA_CHAR_WIDTH_RATIO, maxLines }) {
    const fitWidth = effectiveMaxWidth(maxWidth)
    const lines = splitTextToLinesUnlimited(text, { maxWidth, fontSize, charWidthRatio })

    if (!maxLines || maxLines <= 0 || lines.length <= maxLines) {
        return lines
    }

    if (maxLines === 1) {
        if (lines.length <= 1) {
            return lines
        }
        return [truncateLineWithEllipsis(String(text ?? ""), { maxWidth: fitWidth, fontSize, charWidthRatio, forceEllipsis: true })]
    }

    const kept = lines.slice(0, maxLines - 1)
    const overflow = lines.slice(maxLines - 1).join(" ")
    kept.push(truncateLineWithEllipsis(overflow, { maxWidth: fitWidth, fontSize, charWidthRatio, forceEllipsis: true }))
    return kept
}

/**
 * Pre-split text to fit a max width and line count (react-pdf wrap fallback).
 *
 * @param {{
 *   children: string,
 *   maxWidth: number,
 *   maxLines: number,
 *   style?: object | object[],
 *   charWidthRatio?: number,
 * }} props
 */
export default function BoxLabelWrappedText({
    children,
    style,
    maxWidth,
    maxLines,
    charWidthRatio,
}) {
    const flatStyle = resolveStyle(style)
    const fontSize = flatStyle.fontSize ?? 12
    const text = String(children ?? "")

    if (!text.trim()) {
        return null
    }

    const lines = splitTextToLines(text, {
        maxWidth,
        fontSize,
        charWidthRatio,
        maxLines,
    })

    return (
        <View style={{ width: maxWidth }}>
            {lines.map((line, index) => (
                <Text key={index} style={style} wrap={false}>
                    {line}
                </Text>
            ))}
        </View>
    )
}
